import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Space, Select, Tag, Progress, Modal, Form, InputNumber, message, Row, Col, Avatar, Typography, Tooltip, Radio, Alert, Statistic, Input, Tabs, Popconfirm, Slider, Switch, Upload, DatePicker,
} from 'antd';
import type { UploadProps, RcFile } from 'antd/es/upload';
import {
  RobotOutlined, UserOutlined, SyncOutlined, PlusOutlined, ThunderboltOutlined, FilterOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined, SettingOutlined, MinusCircleOutlined, UploadOutlined, PictureOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { apiGet, apiPost, apiRequest, API_HOST, getToken } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

const ROLE_OPTIONS = [
  { code: 'official', label: '官方号', color: COLORS.primary },
  { code: 'kol', label: 'KOL', color: COLORS.secondary },
  { code: 'veteran', label: '老韭菜', color: COLORS.warning },
  { code: 'newbie', label: '新手小白', color: COLORS.success },
  { code: 'normal', label: '普通用户', color: '#6b7280' },
  { code: 'cheerleader', label: '气氛组', color: COLORS.error },
];

const GENDER_OPTIONS = [
  { code: 'male', label: '男性' },
  { code: 'female', label: '女性' },
  { code: 'neutral', label: '中性' },
];

const ACTIVITY_OPTIONS = [
  { code: 'high', label: '高频' },
  { code: 'medium', label: '中频' },
  { code: 'low', label: '低频' },
  { code: 'lurker', label: '潜水' },
];

const BOT_TYPE_OPTIONS = [
  { code: 1, label: 'Privy', color: COLORS.primary, desc: '高级机器人，支持打赏支付' },
  { code: 2, label: 'EVM', color: COLORS.success, desc: '基础机器人，支持互动评论' },
  { code: 3, label: 'Privy-OKX', color: COLORS.secondary, desc: '使用 OKX Wallet 登录的 Privy 机器人' },
];

// 单机器人质押结果 outcome（后端 WorldCupStakingService.Outcome）→ 友好提示
const STAKE_OUTCOME_TEXT: Record<string, string> = {
  DONE: '质押完成',
  REPORT_FAILED: '已转账，但上报失败（将自动重试上报）',
  TRANSFER_UNKNOWN: '转账结果未知，需人工对账',
  SKIPPED: '已跳过（已完成或余额不足）',
  FAILED: '质押失败（转账前失败，可重试）',
};

const SPEAK_STYLE_OPTIONS = [
  { code: 'professional', label: '专业', color: COLORS.primary },
  { code: 'casual', label: '随性', color: COLORS.success },
  { code: 'funny', label: '幽默', color: COLORS.warning },
  { code: 'tech', label: '技术', color: COLORS.secondary },
];

interface BotProfile {
  profileId: number;
  botId: number;
  roleType: string;
  gender: string;
  activityLevel: string;
  interests: string[];
  speakStyle: string;
  region: string;
  experienceYears: number;
  accountStage: string;
  profileCompleteness: number;
  maturityScore: number;
  userName?: string;
  avatar?: string;
  userId?: number;
  evmWalletAddress?: string;
  solWalletAddress?: string;
  botType?: number;
  botTypeName?: string;
  status?: number;
  tags?: string[];
  lastActiveTime?: string;
  accountCreateTime?: string;
  // 世界杯质押状态（worldcup_stake_status）
  worldcupStakeStatus?: string | null;
  worldcupStakeTxHash?: string | null;
  worldcupStakeError?: string | null;
  worldcupStakedAt?: string | null;
}

// 近 15 天每日新增（/admin/bot/stats/daily-new 返回的行）
interface DailyNewRow {
  date: string;       // 'YYYY-MM-DD'
  privy: number;
  evm: number;
  privyOkx: number;
}

// 近 15 天日活：目标 vs 真实（/admin/privy-dau/daily-stats 返回的行）
interface DailyDauRow {
  date: string;            // 'YYYY-MM-DD'
  target: number | null;   // 当日日活目标；历史无记录为 null
  actual: number;          // 真实日活 already_active（含建号/重登/心跳，可能 > target）
}

// 历史任务列表行（/admin/bot/batch-create/tasks 返回的行）
interface CreateTaskRow {
  taskId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  totalCount: number;
  successCount: number;
  failedCount: number;
  progress: number;
  startTime: string;          // 实际开始时间（拿到锁之后）
  endTime: string;
  scheduledStartTime: string; // 计划开始时间（用户提交时填的）
  type: number | null;
  typeName: string | null;
  recurrenceId: number | null; // 非 null 表示来自"每日计划"模板
}

// 每日计划行（/admin/bot/batch-create/recurrences 返回的行）
interface RecurrenceRow {
  id: number;
  type: number;
  typeName: string;
  minTotalCount: number;       // 每次触发数量范围最小值
  maxTotalCount: number;       // 每次触发数量范围最大值
  minIntervalSec: number;
  maxIntervalSec: number;
  timeOfDay: string;          // 'HH:mm'
  enabled: 0 | 1;
  lastFiredDate: string;      // 'YYYY-MM-DD' 或 ''
  createTime: string;
}

// 模版相关接口
interface ProfileTemplate {
  id: number;
  name: string;
  description: string;
  roleType: string;
  gender: string;
  speakStyle: string;
  activityLevel: string;
  interests: string[];
  experienceYears: number;
  region: string;
  enabled: number;
  usageCount: number;
  createTime: string;
  updateTime: string;
}

interface TemplateRatio {
  templateId: number;
  ratio: number;
}

interface BatchApplyProgress {
  taskId: string;
  status: string;
  totalBots: number;
  processedCount: number;
  progress: number;
  distribution: Array<{
    templateId: number;
    templateName: string;
    targetCount: number;
    doneCount: number;
  }>;
  startTime: string | null;
  endTime: string | null;
  errorMessage: string | null;
}

const PROFILE_STATUS_OPTIONS = [
  { code: 'all', label: '全部' },
  { code: 'none', label: '仅无画像' },
  { code: 'has', label: '仅有画像' },
];

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 60,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string; noPadding?: boolean }> = ({ title, extra, children, style, accentColor = COLORS.primary, noPadding }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: noPadding ? 0 : 24 },
    }}
  >
    {children}
  </Card>
);

const BotManagement: React.FC = () => {
  const [profiles, setProfiles] = useState<BotProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterGender, setFilterGender] = useState<string>('');
  const [filterActivity, setFilterActivity] = useState<string>('');
  const [filterBotType, setFilterBotType] = useState<number | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [batchGenerating, setBatchGenerating] = useState(false);
  // 正在质押的机器人 botId（用于该行按钮 loading；同一时刻只允许一只手动质押进行中）
  const [stakingBotId, setStakingBotId] = useState<number | null>(null);

  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 创建机器人弹窗（只剩一个表单，提交后立刻关闭）
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [form] = Form.useForm();
  const selectedBotType = Form.useWatch('botType', form);

  // 画像编辑状态
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<BotProfile | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm] = Form.useForm();

  // 修改头像弹窗状态
  const [avatarModalVisible, setAvatarModalVisible] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarConfirming, setAvatarConfirming] = useState(false);
  const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string>('');
  const [editingAvatarBot, setEditingAvatarBot] = useState<BotProfile | null>(null);

  // Tab 状态：默认进入 '机器人创建' tab（key='0'）
  const [activeTab, setActiveTab] = useState('0');

  // '机器人创建' tab 的数据：按 type 分组的计数 + 近 15 天新增趋势 + 历史任务表
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});
  const [dailyNew, setDailyNew] = useState<DailyNewRow[]>([]);
  const [dailyDau, setDailyDau] = useState<DailyDauRow[]>([]);
  const [createTasks, setCreateTasks] = useState<CreateTaskRow[]>([]);
  const [recurrences, setRecurrences] = useState<RecurrenceRow[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  // 独立的 ref，不和 pollingRef（创建弹窗的进度轮询）混用，避免互相干扰
  const tasksPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 模版管理状态
  const [templates, setTemplates] = useState<ProfileTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProfileTemplate | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm] = Form.useForm();

  // 批量画像设置状态
  const [batchBotType, setBatchBotType] = useState<number | undefined>(undefined);
  const [batchProfileStatus, setBatchProfileStatus] = useState<string>('none');
  const [templateRatios, setTemplateRatios] = useState<TemplateRatio[]>([]);
  const [previewResult, setPreviewResult] = useState<{ totalBots: number; distribution: any[] } | null>(null);
  const [batchApplyTaskId, setBatchApplyTaskId] = useState<string | null>(null);
  const [batchApplyProgress, setBatchApplyProgress] = useState<BatchApplyProgress | null>(null);
  const [isBatchApplying, setIsBatchApplying] = useState(false);
  const batchPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 兴趣标签（从tag表获取）
  const [interestTags, setInterestTags] = useState<{ value: string; label: string }[]>([]);

  // 清理轮询
  useEffect(() => {
    return () => {
      if (batchPollingRef.current) {
        clearInterval(batchPollingRef.current);
      }
      if (tasksPollingRef.current) {
        clearInterval(tasksPollingRef.current);
      }
    };
  }, []);

  // 获取模版列表
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const data = await apiGet<any>('/api/bot/profile-template');
      if (data.code === 0) {
        setTemplates(data.data || []);
      } else {
        message.error(data.msg || '获取模版列表失败');
      }
    } catch (e: any) {
      message.error('获取模版列表失败: ' + e.message);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // '机器人创建' tab：拉按 type 分组的计数（复用 /admin/bot/stats，data.accounts.byType）
  const fetchTypeCounts = useCallback(async () => {
    try {
      const data = await apiGet<any>('/admin/bot/stats');
      if (data.code === 0) {
        setTypeCounts(data.data?.accounts?.byType || {});
      }
    } catch (e: any) {
      console.error('获取类型计数失败:', e.message);
    }
  }, []);

  // '机器人创建' tab：拉近 15 天每日新增（按 type 分组）
  const fetchDailyNew = useCallback(async () => {
    try {
      const data = await apiGet<any>('/admin/bot/stats/daily-new?days=15');
      if (data.code === 0) {
        setDailyNew(data.data || []);
      }
    } catch (e: any) {
      console.error('获取趋势数据失败:', e.message);
    }
  }, []);

  // '机器人创建' tab：拉近 15 天日活（目标 vs 真实）
  const fetchDailyDau = useCallback(async () => {
    try {
      const data = await apiGet<any>('/admin/privy-dau/daily-stats?days=15');
      if (data.code === 0) {
        setDailyDau(data.data || []);
      }
    } catch (e: any) {
      console.error('获取日活趋势数据失败:', e.message);
    }
  }, []);

  // '机器人创建' tab：拉历史创建任务列表（后端已按 startTime 倒序）。
  // silent=true 时不触发 AntD Table 的 loading 蒙层，避免后台 3s 轮询打断用户的取消/hover 等交互。
  // 只有首次挂载和用户主动操作（点击取消）后才用非静默调用显示 spinner。
  const fetchCreateTasks = useCallback(async (silent: boolean = false) => {
    if (!silent) setTasksLoading(true);
    try {
      const data = await apiGet<any>('/admin/bot/batch-create/tasks');
      if (data.code === 0) {
        setCreateTasks(data.data || []);
      }
    } catch (e: any) {
      console.error('获取任务列表失败:', e.message);
    } finally {
      if (!silent) setTasksLoading(false);
    }
  }, []);

  // 取消正在进行的批量任务（下一次循环边界生效）
  const handleCancelTask = useCallback(async (taskIdToCancel: string) => {
    try {
      const data = await apiPost<any>(`/admin/bot/batch-create/${taskIdToCancel}/cancel`, {});
      if (data.code === 0) {
        message.success('取消请求已发出，将在下一次循环边界生效');
        // 静默刷新：用户刚点完取消，不要再用 spinner 蒙层打断他
        fetchCreateTasks(true);
      } else {
        message.error(data.msg || '取消失败');
      }
    } catch (e: any) {
      message.error('取消失败: ' + e.message);
    }
  }, [fetchCreateTasks]);

  // 删除已结束（cancelled / completed）的任务记录。后端会校验状态。
  // 拉每日计划列表
  const fetchRecurrences = useCallback(async () => {
    try {
      const data = await apiGet<any>('/admin/bot/batch-create/recurrences');
      if (data.code === 0) {
        setRecurrences(data.data || []);
      }
    } catch (e: any) {
      console.error('获取每日计划失败:', e.message);
    }
  }, []);

  // 启用/禁用每日计划
  const handleToggleRecurrence = useCallback(async (id: number, nextEnabled: boolean) => {
    try {
      const data = await apiRequest<any>(`/admin/bot/batch-create/recurrence/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: nextEnabled ? 1 : 0 }),
      });
      if (data.code === 0) {
        message.success(nextEnabled ? '已启用' : '已禁用');
        fetchRecurrences();
      } else {
        message.error(data.msg || '操作失败');
      }
    } catch (e: any) {
      message.error('操作失败: ' + e.message);
    }
  }, [fetchRecurrences]);

  // 删除每日计划（保留历史 task 行作审计）
  const handleDeleteRecurrence = useCallback(async (id: number) => {
    try {
      const data = await apiRequest<any>(`/admin/bot/batch-create/recurrence/${id}`, { method: 'DELETE' });
      if (data.code === 0) {
        message.success('已删除');
        fetchRecurrences();
      } else {
        message.error(data.msg || '删除失败');
      }
    } catch (e: any) {
      message.error('删除失败: ' + e.message);
    }
  }, [fetchRecurrences]);

  const handleDeleteTask = useCallback(async (taskIdToDelete: string) => {
    try {
      const data = await apiRequest<any>(`/admin/bot/batch-create/${taskIdToDelete}`, { method: 'DELETE' });
      if (data.code === 0) {
        message.success('已删除');
        fetchCreateTasks(true);
      } else {
        message.error(data.msg || '删除失败');
      }
    } catch (e: any) {
      message.error('删除失败: ' + e.message);
    }
  }, [fetchCreateTasks]);

  // '机器人创建' tab 的历史任务表列定义。
  // 注意 statusMap 已经覆盖 cancelled；进度条对 failed 显示 exception 状态。
  const createTaskColumns = [
    {
      title: '任务ID',
      dataIndex: 'taskId',
      width: 140,
      render: (id: string, row: CreateTaskRow) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }} copyable={{ text: id, tooltips: ['复制完整 ID', '已复制'] }}>
            {id.slice(0, 8)}
          </Text>
          {row.recurrenceId != null && (
            <Tag color="cyan" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px', margin: 0 }}>每日</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '执行日',
      dataIndex: 'scheduledStartTime',
      width: 100,
      render: (t: string) => {
        if (!t) return <Text type="secondary">-</Text>;
        const d = dayjs(t);
        return <Text style={{ fontSize: 12 }}>{d.isValid() ? d.format('YYYYMMDD') : t}</Text>;
      },
    },
    {
      title: '类型',
      dataIndex: 'typeName',
      width: 110,
      render: (typeName: string | null, row: CreateTaskRow) => {
        if (!typeName) return <Text type="secondary">-</Text>;
        const opt = BOT_TYPE_OPTIONS.find((o) => o.code === row.type);
        return <Tag color={opt?.color}>{typeName}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          pending: { color: 'default', text: '待执行' },
          running: { color: 'processing', text: '执行中' },
          completed: { color: 'success', text: '已完成' },
          failed: { color: 'error', text: '失败' },
          cancelled: { color: 'warning', text: '已取消' },
        };
        const s = statusMap[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '进度',
      dataIndex: 'progress',
      width: 160,
      render: (progress: number, row: CreateTaskRow) => (
        <Progress
          percent={progress}
          size="small"
          status={row.status === 'failed' ? 'exception' : undefined}
        />
      ),
    },
    {
      title: '成功/失败/总数',
      width: 140,
      render: (_: unknown, row: CreateTaskRow) => (
        <Space size={4}>
          <Text style={{ color: COLORS.success }}>{row.successCount}</Text>
          <Text type="secondary">/</Text>
          <Text style={{ color: COLORS.error }}>{row.failedCount}</Text>
          <Text type="secondary">· {row.totalCount}</Text>
        </Space>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      width: 170,
      render: (t: string) => {
        if (!t) return <Text type="secondary">-</Text>;
        const d = dayjs(t);
        return <Text style={{ fontSize: 12 }}>{d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : t}</Text>;
      },
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      width: 170,
      render: (t: string) => {
        if (!t) return <Text type="secondary">-</Text>;
        const d = dayjs(t);
        return <Text style={{ fontSize: 12 }}>{d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : t}</Text>;
      },
    },
    {
      title: '操作',
      width: 100,
      render: (_: unknown, row: CreateTaskRow) => {
        // pending/running：显示"取消"；cancelled/completed：显示"删除"；failed 不显示按钮（留着便于排查）
        if (row.status === 'pending' || row.status === 'running') {
          return (
            <Popconfirm
              title="取消该任务？"
              description="正在创建的这只机器人会完成后再终止"
              okText="取消任务"
              cancelText="返回"
              onConfirm={() => handleCancelTask(row.taskId)}
            >
              <Button type="link" size="small" danger>取消</Button>
            </Popconfirm>
          );
        }
        if (row.status === 'cancelled' || row.status === 'completed') {
          return (
            <Popconfirm
              title="删除该任务记录？"
              description="仅从历史表里移除这条任务，已创建的机器人不受影响"
              okText="删除"
              cancelText="返回"
              onConfirm={() => handleDeleteTask(row.taskId)}
            >
              <Button type="link" size="small" danger>删除</Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  // 获取兴趣标签（从tag表获取topic类型）
  const fetchInterestTags = useCallback(async () => {
    try {
      const data = await apiGet<any>('/api/tag/type/topic');
      if (data.code === 0) {
        const tags = (data.data || []).map((tag: any) => ({
          value: tag.name,
          label: tag.name,
        }));
        setInterestTags(tags);
      }
    } catch (e: any) {
      console.error('获取兴趣标签失败:', e.message);
    }
  }, []);

  // Tab 切换时加载数据
  useEffect(() => {
    if (activeTab === '0') {
      // 机器人创建 tab：拉一次 counts/趋势/tasks（首次允许显示 spinner），并起 3s 轮询
      // 轮询全部静默化，避免 Table loading 蒙层打断用户的取消等交互
      fetchTypeCounts();
      fetchDailyNew();
      fetchDailyDau();
      fetchCreateTasks(false);
      fetchRecurrences();
      tasksPollingRef.current = setInterval(() => {
        fetchCreateTasks(true);
        fetchTypeCounts();
        fetchDailyNew();
        fetchDailyDau();
        fetchRecurrences();
      }, 3000);
    } else if (activeTab === '2' || activeTab === '3') {
      fetchTemplates();
      fetchInterestTags();
    }
    return () => {
      if (tasksPollingRef.current) {
        clearInterval(tasksPollingRef.current);
        tasksPollingRef.current = null;
      }
    };
  }, [activeTab, fetchTypeCounts, fetchDailyNew, fetchDailyDau, fetchCreateTasks, fetchRecurrences, fetchTemplates, fetchInterestTags]);

  // 初始化加载兴趣标签
  useEffect(() => {
    fetchInterestTags();
  }, [fetchInterestTags]);

  const fetchProfiles = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterRole) params.append('roleType', filterRole);
      if (filterGender) params.append('gender', filterGender);
      if (filterActivity) params.append('activityLevel', filterActivity);
      if (filterBotType !== undefined) params.append('botType', filterBotType.toString());
      // 添加分页参数（后端 page 从 0 开始）
      params.append('page', (page - 1).toString());
      params.append('size', pageSize.toString());

      const url = `/api/bot/profile/all${params.toString() ? '?' + params.toString() : ''}`;
      const data = await apiGet<any>(url);

      if (data.code === 0 && data.data) {
        // 处理分页响应
        if (data.data.list) {
          setProfiles(data.data.list);
          setPagination({
            current: page,
            pageSize: pageSize,
            total: data.data.total || 0,
          });
        } else if (Array.isArray(data.data)) {
          setProfiles(data.data);
          setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize,
            total: data.data.length,
          }));
        } else {
          setProfiles([]);
        }
      } else if (Array.isArray(data)) {
        setProfiles(data);
      } else {
        setProfiles([]);
      }
    } catch (e: any) {
      message.error('加载失败: ' + e.message);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole, filterGender, filterActivity, filterBotType]);

  // 筛选条件变化时重新加载（从第一页开始）
  useEffect(() => {
    fetchProfiles(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole, filterGender, filterActivity, filterBotType]);

  // 分页变化处理
  const handleTableChange = (paginationConfig: any) => {
    fetchProfiles(paginationConfig.current, paginationConfig.pageSize);
  };

  // 手动质押单个机器人（机器人列表"质押 / 重试 / 继续上报"按钮）。
  // 调用后端 /admin/worldcup-staking/stake/{botId}，与定时任务同源、幂等：
  // done 跳过；transferred/reported 只续上报；transfer_unknown 不重转（需走对账）。
  const handleStake = async (record: BotProfile) => {
    setStakingBotId(record.botId);
    try {
      const data = await apiPost<any>(`/admin/worldcup-staking/stake/${record.botId}`, {});
      if (data.code === 0) {
        const outcome: string = data.data?.outcome || '';
        const text = `Bot#${record.botId} ${STAKE_OUTCOME_TEXT[outcome] || '操作完成'}`;
        if (outcome === 'DONE') {
          message.success(text);
        } else if (outcome === 'FAILED' || outcome === 'TRANSFER_UNKNOWN' || outcome === 'REPORT_FAILED') {
          message.warning(text);
        } else {
          message.info(text);
        }
        // 刷新当前页，更新该行质押状态
        fetchProfiles(pagination.current, pagination.pageSize);
      } else {
        // 400（类型不支持/批量运行中/未配置等）后端已给出明确原因
        message.error(data.msg || '质押失败');
      }
    } catch (e: any) {
      message.error('质押失败: ' + e.message);
    } finally {
      setStakingBotId(null);
    }
  };

  // 开始创建机器人。
  // 创建请求发出后立刻关闭弹窗，让用户在新 tab 的"创建任务历史"表里观察进度（3s 轮询）。
  // 不再走原来的"弹窗内进度条 + 2s 单任务轮询"路径。
  const handleStartCreate = async (values: any) => {
    setIsCreating(true);
    try {
      // values.scheduledStartTime 是 dayjs 对象（DatePicker 返回的），转 ISO 'YYYY-MM-DDTHH:mm:ss'。
      // 若用户清空了 DatePicker 就不传，后端默认 now。
      const scheduledStartTime = values.scheduledStartTime
        ? values.scheduledStartTime.format('YYYY-MM-DDTHH:mm:ss')
        : undefined;

      const requestBody: any = {
        minCount: values.minCount,
        maxCount: values.maxCount,
        type: values.botType,
        minIntervalSec: values.minIntervalSec,
        maxIntervalSec: values.maxIntervalSec,
        defaultProfile: {
          roleType: values.roleType,
          gender: values.gender,
          activityLevel: values.activityLevel,
        },
      };
      if (scheduledStartTime) requestBody.scheduledStartTime = scheduledStartTime;
      if (values.recurring) requestBody.recurring = true;

      const data = await apiPost<any>('/admin/bot/batch-create-with-profile', requestBody);

      if (data.code === 0) {
        message.success('创建任务已启动，进度可在下方任务列表查看');
        setShowCreateModal(false);
        form.resetFields();
        // 静默刷新：弹窗刚关，不要再叠 spinner
        fetchCreateTasks(true);
      } else {
        message.error(data.msg || '创建任务失败');
      }
    } catch (e: any) {
      message.error('请求失败: ' + e.message);
    } finally {
      setIsCreating(false);
    }
  };

  // 关闭创建弹窗
  const handleCloseCreateModal = () => {
    if (!isCreating) {
      setShowCreateModal(false);
      form.resetFields();
    }
  };

  // 弹窗每次打开时把"执行开始时间"重置为"当前时刻"（dayjs()）。
  // initialValues 是 mount 时一次性求值的，反复打开会拿到陈旧的 dayjs()，所以在这里覆写。
  useEffect(() => {
    if (showCreateModal) {
      form.setFieldsValue({ scheduledStartTime: dayjs() });
    }
  }, [showCreateModal, form]);

  const handleBatchOperation = async (operation: string) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择至少一个机器人');
      return;
    }
    setBatchGenerating(true);
    try {
      const data = await apiPost<any>(`/api/bot/profile/${operation}`, { botIds: selectedRowKeys });
      const okCount = data.generated || data.assigned || data.success || 0;
      const failCount = data.failed || 0;
      const errorList: string[] = Array.isArray(data.errors) ? data.errors : [];
      if (failCount > 0) {
        message.warning(
          `成功 ${okCount} 个，失败 ${failCount} 个${errorList[0] ? `：${errorList[0]}` : ''}`
        );
      } else {
        message.success(`操作完成: ${okCount} 个`);
      }
      // 保持当前页，避免刷回第一页
      fetchProfiles(pagination.current, pagination.pageSize);
    } catch (e: any) {
      message.error('操作失败: ' + e.message);
    } finally {
      setBatchGenerating(false);
    }
  };

  // 打开画像编辑弹窗
  const handleEditProfile = (record: BotProfile) => {
    setEditingProfile(record);
    profileForm.setFieldsValue({
      roleType: record.roleType,
      gender: record.gender,
      speakStyle: record.speakStyle,
      activityLevel: record.activityLevel,
      experienceYears: record.experienceYears,
      region: record.region,
      interests: record.interests || [],
    });
    setShowEditProfileModal(true);
  };

  // 保存画像
  const handleSaveProfile = async (values: any) => {
    if (!editingProfile) return;
    setSavingProfile(true);
    try {
      // interests 现在是 Select mode="multiple" 返回的数组
      const interests = Array.isArray(values.interests) ? values.interests : [];

      const data = await apiRequest<any>(`/api/bot/profile/${editingProfile.botId}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...values,
          interests,
        }),
      });

      if (data.code === 0 || !data.code) {
        message.success('画像保存成功');
        setShowEditProfileModal(false);
        setEditingProfile(null);
        profileForm.resetFields();
        fetchProfiles();
      } else {
        message.error(data.msg || data.message || '保存失败');
      }
    } catch (e: any) {
      message.error('保存失败: ' + e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  // 关闭画像编辑弹窗
  const handleCloseProfileModal = () => {
    setShowEditProfileModal(false);
    setEditingProfile(null);
    profileForm.resetFields();
  };

  // ==================== 修改头像 ====================
  const openAvatarModal = (record: BotProfile) => {
    setEditingAvatarBot(record);
    setPendingAvatarUrl('');
    setAvatarModalVisible(true);
  };

  const closeAvatarModal = () => {
    if (avatarUploading || avatarConfirming) return;
    setAvatarModalVisible(false);
    setPendingAvatarUrl('');
    setEditingAvatarBot(null);
  };

  const beforeAvatarUpload = (file: RcFile): boolean | typeof Upload.LIST_IGNORE => {
    if (!file.type.startsWith('image/')) {
      message.error('只支持图片文件');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('图片大小不能超过 5MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // 上传到 xmarsLive 拿到 URL，仅做预览，不落库
  const customAvatarUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    if (!editingAvatarBot) {
      onError?.(new Error('未选择机器人'));
      return;
    }
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append('file', file as File);
      const token = getToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`${API_HOST}/api/bot/profile/${editingAvatarBot.botId}/upload-avatar-image`, {
        method: 'POST',
        headers,
        body: form,
      });
      if (resp.status === 401) {
        localStorage.removeItem('billygo_token');
        localStorage.removeItem('billygo_user');
        window.location.href = '/login';
        throw new Error('登录已过期，请重新登录');
      }
      const json = await resp.json().catch(() => ({}));
      if (json.code !== 0) throw new Error(json.msg || '上传失败');
      const url = json.data?.url as string;
      setPendingAvatarUrl(url);
      message.success('上传成功，请确认修改');
      onSuccess?.({ url }, file as any);
    } catch (e: any) {
      message.error(e.message);
      onError?.(e);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleConfirmAvatar = async () => {
    if (!editingAvatarBot || !pendingAvatarUrl) return;
    setAvatarConfirming(true);
    try {
      const data = await apiPost<any>(
        `/api/bot/profile/${editingAvatarBot.botId}/update-avatar`,
        { avatar: pendingAvatarUrl }
      );
      if (data.code !== 0) throw new Error(data.msg || '修改失败');
      message.success('修改成功');
      setAvatarModalVisible(false);
      setPendingAvatarUrl('');
      setEditingAvatarBot(null);
      // 保持当前页和分页大小，避免刷回第一页
      fetchProfiles(pagination.current, pagination.pageSize);
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setAvatarConfirming(false);
    }
  };

  // ==================== 模版管理方法 ====================

  // 打开新建/编辑模版弹窗
  const handleOpenTemplateModal = (template?: ProfileTemplate) => {
    if (template) {
      setEditingTemplate(template);
      templateForm.setFieldsValue({
        name: template.name,
        description: template.description,
        roleType: template.roleType,
        gender: template.gender,
        speakStyle: template.speakStyle,
        activityLevel: template.activityLevel,
        experienceYears: template.experienceYears,
        region: template.region,
        interests: template.interests || [],
        enabled: template.enabled === 1,
      });
    } else {
      setEditingTemplate(null);
      templateForm.resetFields();
      templateForm.setFieldsValue({
        roleType: 'normal',
        gender: 'neutral',
        speakStyle: 'casual',
        activityLevel: 'medium',
        enabled: true,
      });
    }
    setShowTemplateModal(true);
  };

  // 保存模版
  const handleSaveTemplate = async (values: any) => {
    setSavingTemplate(true);
    try {
      // interests 已经是数组（来自 Select mode="tags"）
      const interests = Array.isArray(values.interests) ? values.interests : [];
      // enabled 从 boolean 转为数字
      const enabled = values.enabled ? 1 : 0;

      const body = { ...values, interests, enabled };
      const url = editingTemplate ? `/api/bot/profile-template/${editingTemplate.id}` : '/api/bot/profile-template';
      const method = editingTemplate ? 'PUT' : 'POST';

      const data = await apiRequest<any>(url, {
        method,
        body: JSON.stringify(body),
      });

      if (data.code === 0) {
        message.success(editingTemplate ? '模版更新成功' : '模版创建成功');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        templateForm.resetFields();
        fetchTemplates();
      } else {
        message.error(data.msg || '保存失败');
      }
    } catch (e: any) {
      message.error('保存失败: ' + e.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  // 删除模版
  const handleDeleteTemplate = async (id: number) => {
    try {
      const data = await apiRequest<any>(`/api/bot/profile-template/${id}`, { method: 'DELETE' });
      if (data.code === 0) {
        message.success('模版删除成功');
        fetchTemplates();
      } else {
        message.error(data.msg || '删除失败');
      }
    } catch (e: any) {
      message.error('删除失败: ' + e.message);
    }
  };

  // ==================== 批量画像设置方法 ====================

  // 添加模版比例
  const handleAddTemplateRatio = () => {
    const enabledTemplates = templates.filter(t => t.enabled === 1);
    if (enabledTemplates.length === 0) {
      message.warning('没有可用的模版，请先创建模版');
      return;
    }
    const usedIds = templateRatios.map(tr => tr.templateId);
    const availableTemplate = enabledTemplates.find(t => !usedIds.includes(t.id));
    if (!availableTemplate) {
      message.warning('所有模版已添加');
      return;
    }
    setTemplateRatios([...templateRatios, { templateId: availableTemplate.id, ratio: 0 }]);
  };

  // 移除模版比例
  const handleRemoveTemplateRatio = (index: number) => {
    setTemplateRatios(templateRatios.filter((_, i) => i !== index));
  };

  // 更新模版比例
  const handleUpdateTemplateRatio = (index: number, field: 'templateId' | 'ratio', value: number) => {
    const newRatios = [...templateRatios];
    newRatios[index] = { ...newRatios[index], [field]: value };
    setTemplateRatios(newRatios);
  };

  // 计算总比例
  const totalRatio = templateRatios.reduce((sum, tr) => sum + tr.ratio, 0);

  // 预览分配结果
  const handlePreview = async () => {
    if (templateRatios.length === 0) {
      message.warning('请添加至少一个模版');
      return;
    }
    if (totalRatio !== 100) {
      message.warning('比例总和必须等于100%');
      return;
    }

    try {
      const data = await apiPost<any>('/api/bot/profile/preview-apply-template', {
        botType: batchBotType,
        profileStatus: batchProfileStatus,
        templateRatios,
      });
      if (data.code === 0) {
        setPreviewResult(data.data);
      } else {
        message.error(data.msg || '预览失败');
      }
    } catch (e: any) {
      message.error('预览失败: ' + e.message);
    }
  };

  // 执行批量应用
  const handleBatchApply = async () => {
    if (templateRatios.length === 0) {
      message.warning('请添加至少一个模版');
      return;
    }
    if (totalRatio !== 100) {
      message.warning('比例总和必须等于100%');
      return;
    }

    setIsBatchApplying(true);
    try {
      const data = await apiPost<any>('/api/bot/profile/batch-apply-template', {
        botType: batchBotType,
        profileStatus: batchProfileStatus,
        templateRatios,
      });
      if (data.code === 0) {
        setBatchApplyTaskId(data.data.taskId);
        message.success('批量应用任务已启动');
        startBatchApplyPolling(data.data.taskId);
      } else {
        message.error(data.msg || '启动任务失败');
        setIsBatchApplying(false);
      }
    } catch (e: any) {
      message.error('启动任务失败: ' + e.message);
      setIsBatchApplying(false);
    }
  };

  // 轮询批量应用进度
  const startBatchApplyPolling = (taskId: string) => {
    if (batchPollingRef.current) {
      clearInterval(batchPollingRef.current);
    }

    fetchBatchApplyProgress(taskId);

    batchPollingRef.current = setInterval(() => {
      fetchBatchApplyProgress(taskId);
    }, 2000);
  };

  const fetchBatchApplyProgress = async (taskId: string) => {
    try {
      const data = await apiGet<any>(`/api/bot/profile/batch-apply-template/${taskId}/progress`);
      if (data.code === 0) {
        setBatchApplyProgress(data.data);
        if (data.data.status === 'completed' || data.data.status === 'failed') {
          if (batchPollingRef.current) {
            clearInterval(batchPollingRef.current);
            batchPollingRef.current = null;
          }
          setIsBatchApplying(false);
          if (data.data.status === 'completed') {
            message.success(`成功设置 ${data.data.processedCount} 个机器人画像`);
            fetchProfiles();
          }
        }
      }
    } catch (e: any) {
      console.error('轮询失败:', e);
    }
  };

  // 重置批量应用
  const handleResetBatchApply = () => {
    setBatchApplyTaskId(null);
    setBatchApplyProgress(null);
    setPreviewResult(null);
    setTemplateRatios([]);
  };

  const getRoleTag = (role: string) => {
    const option = ROLE_OPTIONS.find((o) => o.code === role);
    return (
      <Tag style={{
        background: `${option?.color}22`,
        border: `1px solid ${option?.color}44`,
        color: option?.color || '#6b7280',
        borderRadius: 6,
      }}>
        {option?.label || role}
      </Tag>
    );
  };

  const getBotTypeTag = (type: number | undefined) => {
    const option = BOT_TYPE_OPTIONS.find((o) => o.code === type);
    if (!option) return <Tag style={{ color: '#666' }}>未知</Tag>;
    return (
      <Tag style={{
        background: `${option.color}22`,
        border: `1px solid ${option.color}44`,
        color: option.color,
        borderRadius: 6,
      }}>
        {option.label}
      </Tag>
    );
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      pending: { color: '#666', icon: <LoadingOutlined />, text: '等待中' },
      running: { color: COLORS.orange, icon: <SyncOutlined spin />, text: '创建中' },
      completed: { color: COLORS.green, icon: <CheckCircleOutlined />, text: '已完成' },
      failed: { color: COLORS.pink, icon: <CloseCircleOutlined />, text: '失败' },
    };
    const c = config[status] || config.pending;
    return (
      <Tag color={c.color} icon={c.icon} style={{ fontWeight: 600 }}>
        {c.text}
      </Tag>
    );
  };

  // 世界杯质押状态标签（与后端 BotAccountEntity.WC_STAKE_* 对应；NULL 视为 pending）
  const getStakeStatusTag = (record: BotProfile) => {
    const status = record.worldcupStakeStatus || 'pending';
    const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      pending: { color: 'default', icon: <MinusCircleOutlined />, text: '待质押' },
      transferred: { color: 'processing', icon: <SyncOutlined spin />, text: '已转账·待上报' },
      reported: { color: 'processing', icon: <SyncOutlined spin />, text: '已上报·待确认' },
      transfer_unknown: { color: 'volcano', icon: <ExclamationCircleOutlined />, text: '转账未知·待对账' },
      done: { color: 'success', icon: <CheckCircleOutlined />, text: '已质押' },
      failed: { color: 'error', icon: <CloseCircleOutlined />, text: '质押失败' },
    };
    const c = config[status] || config.pending;
    const tag = <Tag color={c.color} icon={c.icon} style={{ margin: 0 }}>{c.text}</Tag>;
    // txHash / 错误信息放进 tooltip，便于排查
    const tip = record.worldcupStakeTxHash
      ? `txHash: ${record.worldcupStakeTxHash}`
      : (record.worldcupStakeError || '');
    return tip ? <Tooltip title={tip}>{tag}</Tooltip> : tag;
  };

  const columns = [
    {
      title: '头像',
      dataIndex: 'avatar',
      key: 'avatar',
      width: 70,
      render: (avatar: string) =>
        avatar ? (
          <Avatar src={avatar} size={44} style={{ border: `2px solid ${COLORS.borderDark}` }} />
        ) : (
          <Avatar icon={<UserOutlined />} size={44} style={{
            background: `linear-gradient(135deg, ${COLORS.cardBg}, ${COLORS.darkBg})`,
            border: `2px solid ${COLORS.borderDark}`,
          }} />
        ),
    },
    {
      title: '昵称',
      dataIndex: 'userName',
      key: 'userName',
      width: 160,
      render: (name: string, record: BotProfile) => (
        <div>
          <Text style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 500 }}>{name || `Bot#${record.botId}`}</Text>
          <br />
          <Text style={{ color: '#6e7681', fontSize: 12 }}>ID: {record.botId}</Text>
        </div>
      ),
    },
    {
      title: 'xmarsLive UserId',
      dataIndex: 'userId',
      key: 'userId',
      width: 130,
      render: (uid: number | undefined) =>
        uid ? (
          <Text copyable={{ text: String(uid) }} style={{ color: COLORS.cyan, fontFamily: 'monospace', fontSize: 12 }}>
            {uid}
          </Text>
        ) : (
          <Text style={{ color: '#bbb' }}>—</Text>
        ),
    },
    {
      title: '类型',
      dataIndex: 'botType',
      key: 'botType',
      width: 90,
      render: (type: number) => getBotTypeTag(type),
    },
    {
      title: '角色',
      dataIndex: 'roleType',
      key: 'roleType',
      render: (role: string) => getRoleTag(role),
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender: string) => {
        const opt = GENDER_OPTIONS.find((g) => g.code === gender);
        return <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{opt?.label || gender}</Text>;
      },
    },
    {
      title: '阶段',
      dataIndex: 'accountStage',
      key: 'accountStage',
      render: (stage: string) => (
        <Tag style={{ background: COLORS.darkBg, border: `1px solid ${COLORS.borderDark}`, color: 'rgba(0, 0, 0, 0.45)' }}>
          {stage || '-'}
        </Tag>
      ),
    },
    {
      title: '成熟度',
      dataIndex: 'maturityScore',
      key: 'maturityScore',
      render: (score: number) => (
        <Tooltip title={`${score || 0}%`}>
          <Progress
            percent={score || 0}
            size="small"
            strokeColor={{ '0%': COLORS.cyan, '100%': COLORS.green }}
            trailColor={COLORS.borderDark}
            showInfo={false}
          />
        </Tooltip>
      ),
    },
    {
      title: 'EVM钱包',
      dataIndex: 'evmWalletAddress',
      key: 'evmWalletAddress',
      width: 140,
      render: (addr: string) =>
        addr ? (
          <Tooltip title={addr}>
            <Text style={{ color: COLORS.cyan, fontFamily: 'monospace', fontSize: 12 }}>
              {addr.slice(0, 6)}...{addr.slice(-4)}
            </Text>
          </Tooltip>
        ) : '-',
    },
    {
      title: '说话风格',
      dataIndex: 'speakStyle',
      key: 'speakStyle',
      width: 90,
      render: (style: string) => {
        const opt = SPEAK_STYLE_OPTIONS.find((s) => s.code === style);
        return opt ? (
          <Tag style={{
            background: `${opt.color}22`,
            border: `1px solid ${opt.color}44`,
            color: opt.color,
            borderRadius: 6,
          }}>
            {opt.label}
          </Tag>
        ) : <Text style={{ color: '#6e7681' }}>{style || '-'}</Text>;
      },
    },
    {
      title: '兴趣标签',
      dataIndex: 'interests',
      key: 'interests',
      width: 180,
      render: (interests: string[]) =>
        interests && interests.length > 0 ? (
          <Space size={4} wrap>
            {interests.slice(0, 3).map((tag, idx) => (
              <Tag key={idx} style={{
                background: `${COLORS.yellow}22`,
                border: `1px solid ${COLORS.yellow}44`,
                color: COLORS.yellow,
                borderRadius: 4,
                fontSize: 11,
              }}>
                {tag}
              </Tag>
            ))}
            {interests.length > 3 && (
              <Tooltip title={interests.slice(3).join(', ')}>
                <Tag style={{ background: '#333', border: '1px solid #444', color: '#888', borderRadius: 4, fontSize: 11 }}>
                  +{interests.length - 3}
                </Tag>
              </Tooltip>
            )}
          </Space>
        ) : <Text style={{ color: '#6e7681' }}>-</Text>,
    },
    {
      title: '质押状态',
      key: 'worldcupStake',
      width: 175,
      render: (_: any, record: BotProfile) => {
        // 仅 Privy / Privy-OKX（type 1/3）参与世界杯质押；其余类型不适用
        if (record.botType !== 1 && record.botType !== 3) {
          return <Text style={{ color: '#bbb' }}>不适用</Text>;
        }
        const status = record.worldcupStakeStatus || 'pending';
        // 始终展示质押/重试按钮，任何状态都可点击重试。
        // 后端幂等保证安全：done 跳过、transferred/reported 只续上报、transfer_unknown 不重转，
        // 只有 pending/failed 会真正发起转账（真实资金）。
        const willTransfer = status === 'pending' || status === 'failed';
        const actionLabel = status === 'pending' ? '质押' : '重试';
        const confirmTitle = willTransfer
          ? `确认对 Bot#${record.botId} 执行质押？将从其钱包向固定地址转出代币（真实资金，不可撤销）。`
          : `确认对 Bot#${record.botId} 重试质押？已完成 / 已转账的机器人不会重复转账，仅补做必要步骤。`;
        return (
          <Space direction="vertical" size={4} style={{ alignItems: 'flex-start' }}>
            {getStakeStatusTag(record)}
            <Popconfirm
              title={confirmTitle}
              okText="确认"
              cancelText="取消"
              okButtonProps={{ danger: willTransfer }}
              onConfirm={() => handleStake(record)}
            >
              <Button
                type="link"
                size="small"
                danger={willTransfer}
                icon={<ThunderboltOutlined />}
                loading={stakingBotId === record.botId}
                disabled={stakingBotId !== null && stakingBotId !== record.botId}
                style={{ padding: 0, height: 'auto' }}
              >
                {actionLabel}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: BotProfile) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditProfile(record)}
            style={{ color: COLORS.cyan, padding: 0 }}
          >
            编辑画像
          </Button>
          <Button
            type="link"
            size="small"
            icon={<PictureOutlined />}
            onClick={() => openAvatarModal(record)}
            style={{ color: COLORS.purple, padding: 0 }}
          >
            修改头像
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader icon={<RobotOutlined />} title="机器人管理" subtitle="管理机器人画像、模版配置和批量设置" color={COLORS.purple} />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginBottom: 24 }}
        items={[
          {
            key: '0',
            label: (
              <Space>
                <PlusOutlined />
                <span>机器人创建</span>
              </Space>
            ),
            children: (
              <>
                {/* 按类型总数（紧凑卡片） */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  {BOT_TYPE_OPTIONS.map((opt) => (
                    <Col span={8} key={opt.code}>
                      <SimpleCard accentColor={opt.color} noPadding>
                        <div style={{
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <Tag color={opt.color} style={{ alignSelf: 'flex-start', margin: 0 }}>{opt.label}</Tag>
                            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {opt.desc}
                            </Text>
                          </div>
                          <span style={{ color: opt.color, fontSize: 22, fontWeight: 600, lineHeight: 1 }}>
                            {typeCounts[String(opt.code)] ?? 0}
                          </span>
                        </div>
                      </SimpleCard>
                    </Col>
                  ))}
                </Row>

                {/* 近 15 天每日新增（按 type 堆叠柱状图） */}
                <SimpleCard
                  title={
                    <Space size={16}>
                      <span>近 15 天每日新增</span>
                      <Space size={12}>
                        {BOT_TYPE_OPTIONS.map((opt) => (
                          <Space key={opt.code} size={4}>
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: opt.color }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>{opt.label}</Text>
                          </Space>
                        ))}
                      </Space>
                    </Space>
                  }
                  accentColor={COLORS.primary}
                  style={{ marginBottom: 16 }}
                >
                  {(() => {
                    const maxTotal = Math.max(
                      ...dailyNew.map((d) => d.privy + d.evm + d.privyOkx),
                      1,
                    );
                    const BAR_AREA_PX = 110;  // 留出顶端 ~20px 显示当日合计数字
                    return (
                      <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, height: 170, padding: '4px 0' }}>
                        {dailyNew.length === 0 ? (
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text type="secondary">暂无数据</Text>
                          </div>
                        ) : (
                          dailyNew.map((day) => {
                            const total = day.privy + day.evm + day.privyOkx;
                            const barHeight = total === 0 ? 0 : Math.max(2, (total / maxTotal) * BAR_AREA_PX);
                            // Tooltip 内文字固定深色背景上的浅色，避免被柱子色穿透
                            const tip = (
                              <div style={{ fontSize: 12, minWidth: 140, color: '#fff' }}>
                                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{day.date}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, lineHeight: 1.8 }}>
                                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: BOT_TYPE_OPTIONS[0].color, marginRight: 6 }} />Privy</span>
                                  <b>{day.privy}</b>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, lineHeight: 1.8 }}>
                                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: BOT_TYPE_OPTIONS[1].color, marginRight: 6 }} />EVM</span>
                                  <b>{day.evm}</b>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, lineHeight: 1.8 }}>
                                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: BOT_TYPE_OPTIONS[2].color, marginRight: 6 }} />Privy-OKX</span>
                                  <b>{day.privyOkx}</b>
                                </div>
                                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.3)', display: 'flex', justifyContent: 'space-between' }}>
                                  <span>合计</span>
                                  <b>{total}</b>
                                </div>
                              </div>
                            );
                            return (
                              <Tooltip
                                key={day.date}
                                title={tip}
                                mouseEnterDelay={0.05}
                                color="#1f2937"
                                placement="top"
                              >
                                <div style={{
                                  flex: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  cursor: 'default',
                                  minHeight: 0,
                                }}>
                                  {/* 柱子顶端直接显示当日合计数字（>0 才显示，避免一排 0 视觉噪声） */}
                                  <Text style={{
                                    fontSize: 11,
                                    color: total > 0 ? 'rgba(0,0,0,0.65)' : 'transparent',
                                    marginBottom: 4,
                                    lineHeight: 1,
                                    fontWeight: 500,
                                  }}>
                                    {total > 0 ? total : ''}
                                  </Text>
                                  <div style={{
                                    width: '70%',
                                    maxWidth: 28,
                                    height: barHeight,
                                    minHeight: total === 0 ? 4 : 2,
                                    display: 'flex',
                                    flexDirection: 'column-reverse',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    background: total === 0 ? '#f0f0f0' : undefined,
                                  }}>
                                    {day.privy > 0 && <div style={{ flexGrow: day.privy, background: BOT_TYPE_OPTIONS[0].color }} />}
                                    {day.evm > 0 && <div style={{ flexGrow: day.evm, background: BOT_TYPE_OPTIONS[1].color }} />}
                                    {day.privyOkx > 0 && <div style={{ flexGrow: day.privyOkx, background: BOT_TYPE_OPTIONS[2].color }} />}
                                  </div>
                                  <Text style={{ fontSize: 10, color: '#999', marginTop: 6 }}>{day.date.slice(5)}</Text>
                                </div>
                              </Tooltip>
                            );
                          })
                        )}
                      </div>
                    );
                  })()}
                </SimpleCard>

                {/* 近 15 日机器人平台创造日活数：取 max(目标, 真实)，真实=already_active（建号+刷token+心跳） */}
                <SimpleCard
                  title="近 15 日机器人平台创造日活数（真实日活=privy平台查看日活数-机器人平台创造日活数）"
                  accentColor={COLORS.purple}
                  style={{ marginBottom: 16 }}
                >
                  {(() => {
                    const dauMax = Math.max(
                      ...dailyDau.map((d) => Math.max(d.actual, d.target ?? 0)),
                      1,
                    );
                    const BAR_AREA_PX = 100;  // 柱区高度；上方留白显示实际值
                    return (
                      <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, height: 170, padding: '4px 0' }}>
                        {dailyDau.length === 0 ? (
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text type="secondary">暂无数据</Text>
                          </div>
                        ) : (
                          dailyDau.map((day) => {
                            const value = Math.max(day.actual, day.target ?? 0);
                            const barH = value === 0 ? 0 : Math.max(2, (value / dauMax) * BAR_AREA_PX);
                            const tip = (
                              <div style={{ fontSize: 12, minWidth: 120, color: '#fff' }}>
                                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{day.date}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, lineHeight: 1.8 }}>
                                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: COLORS.primary, marginRight: 6 }} />日活数</span>
                                  <b>{value}</b>
                                </div>
                              </div>
                            );
                            return (
                              <Tooltip
                                key={day.date}
                                title={tip}
                                mouseEnterDelay={0.05}
                                color="#1f2937"
                                placement="top"
                              >
                                <div style={{
                                  flex: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  cursor: 'default',
                                  minHeight: 0,
                                }}>
                                  {/* 柱区：固定高度，单柱自底向上 */}
                                  <div style={{ position: 'relative', height: BAR_AREA_PX, width: '70%', maxWidth: 28 }}>
                                    {/* 顶端日活数（贴柱顶） */}
                                    {value > 0 && (
                                      <Text style={{
                                        position: 'absolute',
                                        bottom: barH,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        fontSize: 11,
                                        lineHeight: 1,
                                        marginBottom: 2,
                                        fontWeight: 500,
                                        color: 'rgba(0,0,0,0.65)',
                                        whiteSpace: 'nowrap',
                                      }}>
                                        {value}
                                      </Text>
                                    )}
                                    {/* 单柱：max(目标, 真实) */}
                                    <div style={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: barH,
                                      minHeight: value === 0 ? 0 : 2,
                                      borderRadius: 4,
                                      background: COLORS.primary,
                                    }} />
                                  </div>
                                  <Text style={{ fontSize: 10, color: '#999', marginTop: 6 }}>{day.date.slice(5)}</Text>
                                </div>
                              </Tooltip>
                            );
                          })
                        )}
                      </div>
                    );
                  })()}
                </SimpleCard>

                {/* 每日计划（仅当有 recurrence 才展示） */}
                {recurrences.length > 0 && (
                  <SimpleCard
                    title={
                      <Space>
                        <SyncOutlined style={{ color: COLORS.cyan }} />
                        <span>每日计划</span>
                        <Tag>{recurrences.length}</Tag>
                      </Space>
                    }
                    accentColor={COLORS.cyan}
                    style={{ marginBottom: 24 }}
                    noPadding
                  >
                    <Table
                      dataSource={recurrences}
                      rowKey="id"
                      pagination={false}
                      size="middle"
                      columns={[
                        {
                          title: '类型', dataIndex: 'typeName', width: 110,
                          render: (typeName: string, row: RecurrenceRow) => {
                            const opt = BOT_TYPE_OPTIONS.find((o) => o.code === row.type);
                            return <Tag color={opt?.color}>{typeName}</Tag>;
                          },
                        },
                        {
                          title: '每天', dataIndex: 'timeOfDay', width: 90,
                          render: (t: string) => <Text code style={{ fontSize: 12 }}>{t}</Text>,
                        },
                        {
                          title: '数量', width: 90,
                          render: (_: unknown, row: RecurrenceRow) => (
                            row.minTotalCount === row.maxTotalCount
                              ? <Text>{row.minTotalCount}</Text>
                              : <Text style={{ fontSize: 12 }}>{row.minTotalCount}~{row.maxTotalCount}</Text>
                          ),
                        },
                        {
                          title: '间隔', width: 110,
                          render: (_: unknown, row: RecurrenceRow) => (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {row.minIntervalSec}~{row.maxIntervalSec}s
                            </Text>
                          ),
                        },
                        {
                          title: '上次触发', dataIndex: 'lastFiredDate', width: 120,
                          render: (d: string) => d
                            ? <Text style={{ fontSize: 12 }}>{d}</Text>
                            : <Text type="secondary">-</Text>,
                        },
                        {
                          title: '启用', dataIndex: 'enabled', width: 90,
                          render: (en: number, row: RecurrenceRow) => (
                            <Switch
                              size="small"
                              checked={en === 1}
                              onChange={(checked) => handleToggleRecurrence(row.id, checked)}
                              checkedChildren="启用"
                              unCheckedChildren="禁用"
                            />
                          ),
                        },
                        {
                          title: '操作', width: 90,
                          render: (_: unknown, row: RecurrenceRow) => (
                            <Popconfirm
                              title="删除该每日计划？"
                              description="已创建的机器人不受影响；今天若未触发也不再触发"
                              okText="删除"
                              cancelText="返回"
                              onConfirm={() => handleDeleteRecurrence(row.id)}
                            >
                              <Button type="link" size="small" danger>删除</Button>
                            </Popconfirm>
                          ),
                        },
                      ]}
                    />
                  </SimpleCard>
                )}

                {/* 创建入口 + 任务历史 */}
                <SimpleCard
                  title={
                    <Space>
                      <RobotOutlined style={{ color: COLORS.purple }} />
                      <span>创建任务历史</span>
                      <Tag>{createTasks.length}</Tag>
                    </Space>
                  }
                  extra={
                    <Button
                      icon={<PlusOutlined />}
                      type="primary"
                      style={{ background: COLORS.success, borderColor: COLORS.success }}
                      onClick={() => setShowCreateModal(true)}
                    >
                      创建机器人
                    </Button>
                  }
                  accentColor={COLORS.purple}
                  noPadding
                >
                  <Table
                    dataSource={createTasks}
                    rowKey="taskId"
                    loading={tasksLoading}
                    pagination={{ pageSize: 10 }}
                    columns={createTaskColumns}
                    size="middle"
                  />
                </SimpleCard>
              </>
            ),
          },
          {
            key: '1',
            label: (
              <Space>
                <RobotOutlined />
                <span>机器人列表</span>
              </Space>
            ),
            children: (
              <>
                {/* 筛选区域 */}
                <SimpleCard style={{ marginBottom: 24 }} accentColor={COLORS.purple}>
                  <Row gutter={[16, 16]} align="middle">
                    <Col>
                      <Space>
                        <FilterOutlined style={{ color: COLORS.purple }} />
                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>筛选:</Text>
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text style={{ color: '#6e7681', fontSize: 13 }}>类型</Text>
                        <Select
                          value={filterBotType}
                          onChange={setFilterBotType}
                          style={{ width: 100 }}
                          allowClear
                          placeholder="全部"
                          options={BOT_TYPE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text style={{ color: '#6e7681', fontSize: 13 }}>角色</Text>
                        <Select
                          value={filterRole}
                          onChange={setFilterRole}
                          style={{ width: 120 }}
                          allowClear
                          placeholder="全部"
                          options={[{ value: '', label: '全部' }, ...ROLE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))]}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text style={{ color: '#6e7681', fontSize: 13 }}>性别</Text>
                        <Select
                          value={filterGender}
                          onChange={setFilterGender}
                          style={{ width: 100 }}
                          allowClear
                          placeholder="全部"
                          options={[{ value: '', label: '全部' }, ...GENDER_OPTIONS.map((o) => ({ value: o.code, label: o.label }))]}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text style={{ color: '#6e7681', fontSize: 13 }}>活跃度</Text>
                        <Select
                          value={filterActivity}
                          onChange={setFilterActivity}
                          style={{ width: 100 }}
                          allowClear
                          placeholder="全部"
                          options={[{ value: '', label: '全部' }, ...ACTIVITY_OPTIONS.map((o) => ({ value: o.code, label: o.label }))]}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Button onClick={() => fetchProfiles(1, pagination.pageSize)} icon={<SyncOutlined />} type="primary">
                        查询
                      </Button>
                    </Col>
                  </Row>
                </SimpleCard>

                {/* 批量操作 */}
                <SimpleCard style={{ marginBottom: 24 }} accentColor={COLORS.pink}>
                  <Space wrap size="middle">
                    <Button
                      icon={<ThunderboltOutlined />}
                      onClick={() => handleBatchOperation('batch-generate-random-nickname')}
                      loading={batchGenerating}
                      disabled={selectedRowKeys.length === 0}
                      style={{ color: selectedRowKeys.length === 0 ? undefined : COLORS.primary }}
                    >
                      AI生成昵称
                      {selectedRowKeys.length > 0 && (
                        <Tag style={{ marginLeft: 8, background: `${COLORS.cyan}22`, border: 'none', color: COLORS.cyan }}>
                          {selectedRowKeys.length}
                        </Tag>
                      )}
                    </Button>
                  </Space>
                </SimpleCard>

                {/* 机器人列表 */}
                <SimpleCard
                  title={
                    <Space>
                      <RobotOutlined style={{ color: COLORS.purple }} />
                      <span>机器人列表</span>
                      <Tag style={{
                        background: `${COLORS.purple}22`,
                        border: `1px solid ${COLORS.purple}44`,
                        color: COLORS.purple,
                        boxShadow: `0 0 8px ${COLORS.purple}33`,
                      }}>
                        {pagination.total}
                      </Tag>
                    </Space>
                  }
                  accentColor={COLORS.purple}
                  noPadding
                >
                  <Table
                    rowSelection={{
                      selectedRowKeys,
                      onChange: (keys) => setSelectedRowKeys(keys as number[]),
                      getCheckboxProps: (record: BotProfile) => ({
                        name: record.botId.toString(),
                      }),
                    }}
                    columns={columns}
                    dataSource={profiles}
                    rowKey="botId"
                    loading={loading}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (total, range) => (
                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                          第 {range[0]}-{range[1]} 条，共 {total} 条
                        </Text>
                      ),
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    style={{ background: 'transparent' }}
                  />
                </SimpleCard>
              </>
            ),
          },
          {
            key: '2',
            label: (
              <Space>
                <AppstoreOutlined />
                <span>画像模版</span>
              </Space>
            ),
            children: (
              <>
                {/* 模版管理 */}
                <SimpleCard
                  title={
                    <Space>
                      <AppstoreOutlined style={{ color: COLORS.cyan }} />
                      <span>画像模版管理</span>
                      <Tag style={{
                        background: `${COLORS.cyan}22`,
                        border: `1px solid ${COLORS.cyan}44`,
                        color: COLORS.cyan,
                      }}>
                        {templates.length}
                      </Tag>
                    </Space>
                  }
                  extra={
                    <Button icon={<PlusOutlined />} onClick={() => handleOpenTemplateModal()} type="primary" style={{ background: COLORS.success, borderColor: COLORS.success }}>
                      新建模版
                    </Button>
                  }
                  accentColor={COLORS.cyan}
                  noPadding
                >
                  <Table
                    dataSource={templates}
                    rowKey="id"
                    loading={templatesLoading}
                    pagination={false}
                    columns={[
                      {
                        title: '模版名称',
                        dataIndex: 'name',
                        key: 'name',
                        render: (name: string, record: ProfileTemplate) => (
                          <div>
                            <Text style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 500 }}>{name}</Text>
                            {record.description && (
                              <div>
                                <Text style={{ color: '#6e7681', fontSize: 12 }}>{record.description}</Text>
                              </div>
                            )}
                          </div>
                        ),
                      },
                      { title: '角色', dataIndex: 'roleType', key: 'roleType', render: (role: string) => getRoleTag(role) },
                      {
                        title: '性别',
                        dataIndex: 'gender',
                        key: 'gender',
                        render: (gender: string) => {
                          const opt = GENDER_OPTIONS.find((g) => g.code === gender);
                          return <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{opt?.label || gender}</Text>;
                        },
                      },
                      {
                        title: '风格',
                        dataIndex: 'speakStyle',
                        key: 'speakStyle',
                        render: (style: string) => {
                          const opt = SPEAK_STYLE_OPTIONS.find((s) => s.code === style);
                          return opt ? (
                            <Tag style={{
                              background: `${opt.color}22`,
                              border: `1px solid ${opt.color}44`,
                              color: opt.color,
                              borderRadius: 6,
                            }}>
                              {opt.label}
                            </Tag>
                          ) : <Text style={{ color: '#6e7681' }}>{style || '-'}</Text>;
                        },
                      },
                      {
                        title: '活跃度',
                        dataIndex: 'activityLevel',
                        key: 'activityLevel',
                        render: (level: string) => {
                          const opt = ACTIVITY_OPTIONS.find((a) => a.code === level);
                          return <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{opt?.label || level}</Text>;
                        },
                      },
                      {
                        title: '兴趣标签',
                        dataIndex: 'interests',
                        key: 'interests',
                        width: 180,
                        render: (interests: string[]) =>
                          interests && interests.length > 0 ? (
                            <Space size={4} wrap>
                              {interests.slice(0, 3).map((tag, idx) => (
                                <Tag key={idx} style={{
                                  background: `${COLORS.yellow}22`,
                                  border: `1px solid ${COLORS.yellow}44`,
                                  color: COLORS.yellow,
                                  borderRadius: 4,
                                  fontSize: 11,
                                }}>
                                  {tag}
                                </Tag>
                              ))}
                              {interests.length > 3 && (
                                <Tooltip title={interests.slice(3).join(', ')}>
                                  <Tag style={{ background: '#333', border: '1px solid #444', color: '#888', borderRadius: 4, fontSize: 11 }}>
                                    +{interests.length - 3}
                                  </Tag>
                                </Tooltip>
                              )}
                            </Space>
                          ) : <Text style={{ color: '#6e7681' }}>-</Text>,
                      },
                      {
                        title: '使用次数',
                        dataIndex: 'usageCount',
                        key: 'usageCount',
                        render: (count: number) => (
                          <Tag style={{ background: `${COLORS.yellow}22`, border: `1px solid ${COLORS.yellow}44`, color: COLORS.yellow }}>
                            {count || 0}
                          </Tag>
                        ),
                      },
                      {
                        title: '状态',
                        dataIndex: 'enabled',
                        key: 'enabled',
                        render: (enabled: number) => (
                          <Tag style={{
                            background: enabled === 1 ? `${COLORS.green}22` : '#33333366',
                            border: `1px solid ${enabled === 1 ? COLORS.green : '#666'}44`,
                            color: enabled === 1 ? COLORS.green : '#666',
                          }}>
                            {enabled === 1 ? '启用' : '禁用'}
                          </Tag>
                        ),
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 150,
                        render: (_: any, record: ProfileTemplate) => (
                          <Space>
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={() => handleOpenTemplateModal(record)}
                              style={{ color: COLORS.cyan, padding: 0 }}
                            >
                              编辑
                            </Button>
                            <Popconfirm
                              title="确定删除此模版吗？"
                              onConfirm={() => handleDeleteTemplate(record.id)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button type="link" icon={<DeleteOutlined />} style={{ color: COLORS.pink, padding: 0 }}>
                                删除
                              </Button>
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </SimpleCard>
              </>
            ),
          },
          {
            key: '3',
            label: (
              <Space>
                <SettingOutlined />
                <span>批量画像设置</span>
              </Space>
            ),
            children: (
              <>
                {/* 批量画像设置 */}
                <Row gutter={24}>
                  <Col span={16}>
                    <SimpleCard
                      title={
                        <Space>
                          <SettingOutlined style={{ color: COLORS.orange }} />
                          <span>批量画像设置</span>
                        </Space>
                      }
                      accentColor={COLORS.orange}
                    >
                      {/* 筛选条件 */}
                      <div style={{ marginBottom: 24 }}>
                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 12, display: 'block' }}>筛选条件</Text>
                        <Space size="large">
                          <Space>
                            <Text style={{ color: '#6e7681', fontSize: 13 }}>机器人类型</Text>
                            <Select
                              value={batchBotType}
                              onChange={setBatchBotType}
                              style={{ width: 120 }}
                              allowClear
                              placeholder="全部"
                              options={BOT_TYPE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                            />
                          </Space>
                          <Space>
                            <Text style={{ color: '#6e7681', fontSize: 13 }}>画像状态</Text>
                            <Select
                              value={batchProfileStatus}
                              onChange={setBatchProfileStatus}
                              style={{ width: 120 }}
                              options={PROFILE_STATUS_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                            />
                          </Space>
                        </Space>
                      </div>

                      {/* 模版比例设置 */}
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>模版比例设置</Text>
                          <Button
                            icon={<PlusOutlined />}
                            onClick={handleAddTemplateRatio}
                            style={{ color: COLORS.primary }}
                            disabled={isBatchApplying}
                          >
                            添加模版
                          </Button>
                        </div>

                        {templateRatios.length === 0 ? (
                          <div style={{
                            padding: 40,
                            textAlign: 'center',
                            background: COLORS.darkBg,
                            borderRadius: 8,
                            border: `1px dashed ${COLORS.borderDark}`,
                          }}>
                            <Text style={{ color: '#6e7681' }}>请点击"添加模版"按钮添加模版比例配置</Text>
                          </div>
                        ) : (
                          <div style={{ background: COLORS.darkBg, padding: 16, borderRadius: 8 }}>
                            {templateRatios.map((tr, index) => (
                              <div key={index} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 16,
                                marginBottom: index < templateRatios.length - 1 ? 16 : 0,
                                padding: 12,
                                background: COLORS.cardBg,
                                borderRadius: 6,
                                border: `1px solid ${COLORS.borderDark}`,
                              }}>
                                <Select
                                  value={tr.templateId}
                                  onChange={(val) => handleUpdateTemplateRatio(index, 'templateId', val)}
                                  style={{ width: 200 }}
                                  disabled={isBatchApplying}
                                  options={templates.filter(t => t.enabled === 1).map(t => ({
                                    value: t.id,
                                    label: t.name,
                                    disabled: templateRatios.some((r, i) => i !== index && r.templateId === t.id),
                                  }))}
                                />
                                <div style={{ flex: 1 }}>
                                  <Slider
                                    value={tr.ratio}
                                    onChange={(val) => handleUpdateTemplateRatio(index, 'ratio', val)}
                                    min={0}
                                    max={100}
                                    disabled={isBatchApplying}
                                    tooltip={{ formatter: (val) => `${val}%` }}
                                  />
                                </div>
                                <InputNumber
                                  value={tr.ratio}
                                  onChange={(val) => handleUpdateTemplateRatio(index, 'ratio', val || 0)}
                                  min={0}
                                  max={100}
                                  style={{ width: 80 }}
                                  disabled={isBatchApplying}
                                  addonAfter="%"
                                />
                                <Button
                                  type="text"
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => handleRemoveTemplateRatio(index)}
                                  style={{ color: COLORS.pink }}
                                  disabled={isBatchApplying}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* 总比例提示 */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 12,
                        background: totalRatio === 100 ? `${COLORS.green}15` : `${COLORS.pink}15`,
                        borderRadius: 6,
                        border: `1px solid ${totalRatio === 100 ? COLORS.green : COLORS.pink}44`,
                        marginBottom: 24,
                      }}>
                        <Text style={{ color: totalRatio === 100 ? COLORS.green : COLORS.pink }}>
                          总比例: {totalRatio}%
                        </Text>
                        {totalRatio !== 100 && (
                          <Text style={{ color: COLORS.pink, fontSize: 12 }}>
                            比例总和必须等于 100%
                          </Text>
                        )}
                      </div>

                      {/* 操作按钮 */}
                      <Space>
                        <Button
                          onClick={handlePreview}
                          type="primary"
                          disabled={templateRatios.length === 0 || totalRatio !== 100 || isBatchApplying}
                        >
                          预览分配结果
                        </Button>
                        <Button
                          type="primary"
                          onClick={handleBatchApply}
                          loading={isBatchApplying}
                          style={{ background: COLORS.success, borderColor: COLORS.success }}
                          disabled={templateRatios.length === 0 || totalRatio !== 100}
                        >
                          确认设置画像
                        </Button>
                        {(batchApplyTaskId || previewResult) && (
                          <Button onClick={handleResetBatchApply} disabled={isBatchApplying}>
                            重置
                          </Button>
                        )}
                      </Space>
                    </SimpleCard>
                  </Col>

                  <Col span={8}>
                    {/* 预览/进度面板 */}
                    {batchApplyProgress ? (
                      <SimpleCard
                        title={
                          <Space>
                            <SyncOutlined spin={batchApplyProgress.status === 'running'} />
                            <span>执行进度</span>
                          </Space>
                        }
                        accentColor={COLORS.cyan}
                      >
                        <div style={{ marginBottom: 16 }}>
                          {getStatusTag(batchApplyProgress.status)}
                        </div>
                        <Progress
                          percent={batchApplyProgress.progress}
                          strokeColor={{ '0%': COLORS.cyan, '100%': COLORS.green }}
                          trailColor="#333"
                        />
                        <Row gutter={16} style={{ marginTop: 16 }}>
                          <Col span={12}>
                            <Statistic
                              title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>总数</Text>}
                              value={batchApplyProgress.totalBots}
                              valueStyle={{ color: 'rgba(0, 0, 0, 0.85)', fontSize: 20 }}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>已处理</Text>}
                              value={batchApplyProgress.processedCount}
                              valueStyle={{ color: COLORS.green, fontSize: 20 }}
                            />
                          </Col>
                        </Row>
                        {batchApplyProgress.distribution && (
                          <div style={{ marginTop: 16 }}>
                            <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 8, display: 'block' }}>分配详情</Text>
                            {batchApplyProgress.distribution.map((d, i) => (
                              <div key={i} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '8px 0',
                                borderBottom: i < batchApplyProgress.distribution.length - 1 ? `1px solid ${COLORS.borderDark}` : 'none',
                              }}>
                                <Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{d.templateName}</Text>
                                <Text style={{ color: COLORS.cyan }}>{d.doneCount}/{d.targetCount}</Text>
                              </div>
                            ))}
                          </div>
                        )}
                        {batchApplyProgress.errorMessage && (
                          <Alert
                            type="error"
                            message={batchApplyProgress.errorMessage}
                            style={{ marginTop: 16, background: `${COLORS.pink}15`, border: `1px solid ${COLORS.pink}40` }}
                          />
                        )}
                      </SimpleCard>
                    ) : previewResult ? (
                      <SimpleCard
                        title={
                          <Space>
                            <CheckCircleOutlined style={{ color: COLORS.green }} />
                            <span>预览结果</span>
                          </Space>
                        }
                        accentColor={COLORS.green}
                      >
                        <Statistic
                          title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>匹配机器人数量</Text>}
                          value={previewResult.totalBots}
                          valueStyle={{ color: COLORS.cyan, fontSize: 32 }}
                          suffix="个"
                        />
                        <div style={{ marginTop: 24 }}>
                          <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 8, display: 'block' }}>分配预览</Text>
                          {previewResult.distribution.map((d: any, i: number) => (
                            <div key={i} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 0',
                              borderBottom: i < previewResult.distribution.length - 1 ? `1px solid ${COLORS.borderDark}` : 'none',
                            }}>
                              <Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{d.templateName}</Text>
                              <Text style={{ color: COLORS.green }}>{d.count} 个</Text>
                            </div>
                          ))}
                        </div>
                      </SimpleCard>
                    ) : (
                      <SimpleCard accentColor={COLORS.borderDark}>
                        <div style={{ textAlign: 'center', padding: 40 }}>
                          <SettingOutlined style={{ fontSize: 48, color: '#444', marginBottom: 16 }} />
                          <div>
                            <Text style={{ color: '#6e7681' }}>配置模版比例后点击"预览"查看分配结果</Text>
                          </div>
                        </div>
                      </SimpleCard>
                    )}
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />

      {/* 创建机器人弹窗 */}
      <Modal
        title={
          <Space>
            <PlusOutlined style={{ color: COLORS.green }} />
            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>创建机器人</span>
          </Space>
        }
        open={showCreateModal}
        onCancel={handleCloseCreateModal}
        footer={null}
        width={600}
        maskClosable={!isCreating}
        closable={!isCreating}
        styles={{ body: { paddingTop: 24 } }}
      >
        <Form
          form={form}
          onFinish={handleStartCreate}
          layout="vertical"
          initialValues={{
            minCount: 10,
            maxCount: 10,
            botType: 2, // 默认 EVM
            roleType: 'normal',
              gender: 'neutral',
              activityLevel: 'medium',
              minIntervalSec: 2,
              maxIntervalSec: 5,
              recurring: false,
            }}
          >
            <Form.Item
              label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>执行开始时间</Text>}
              name="scheduledStartTime"
              rules={[{ required: true, message: '请选择执行开始时间' }]}
              extra={
                <Text style={{ color: '#6e7681', fontSize: 12 }}>
                  默认当前时刻，可选未来；全局同时只跑一个任务，其余按 FIFO 排队
                </Text>
              }
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder="选择执行开始时间"
              />
            </Form.Item>

            <Form.Item
              label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>每天自动执行</Text>}
              name="recurring"
              valuePropName="checked"
              extra={
                <Text style={{ color: '#6e7681', fontSize: 12 }}>
                  开启后：首次按上方时间执行，之后每天同一时刻（分钟精度）自动创建同样配置的任务
                </Text>
              }
            >
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>

            <Form.Item
              label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>创建数量范围</Text>}
              required
              extra={
                <Text style={{ color: '#6e7681', fontSize: 12 }}>
                  每次触发时在该范围内随机取数；若两端相等就是固定值
                </Text>
              }
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="minCount"
                    noStyle
                    rules={[{ required: true, type: 'number', min: 1, max: 10000, message: '范围 1 ~ 10000' }]}
                  >
                    <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="最小值" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="maxCount"
                    noStyle
                    dependencies={['minCount']}
                    rules={[
                      { required: true, type: 'number', min: 1, max: 10000, message: '范围 1 ~ 10000' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const min = getFieldValue('minCount');
                          if (value == null || min == null || value >= min) return Promise.resolve();
                          return Promise.reject(new Error('最大值不能小于最小值'));
                        },
                      }),
                    ]}
                  >
                    <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="最大值" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>创建间隔（秒，单个创建后随机停顿）</Text>}
              required
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="minIntervalSec"
                    noStyle
                    rules={[{ required: true, type: 'number', min: 0, max: 3600, message: '范围 0 ~ 3600' }]}
                  >
                    <InputNumber min={0} max={3600} style={{ width: '100%' }} placeholder="最小值" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="maxIntervalSec"
                    noStyle
                    dependencies={['minIntervalSec']}
                    rules={[
                      { required: true, type: 'number', min: 0, max: 3600, message: '范围 0 ~ 3600' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const min = getFieldValue('minIntervalSec');
                          if (value == null || min == null || value >= min) return Promise.resolve();
                          return Promise.reject(new Error('最大值不能小于最小值'));
                        },
                      }),
                    ]}
                  >
                    <InputNumber min={0} max={3600} style={{ width: '100%' }} placeholder="最大值" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>机器人类型</Text>}
              name="botType"
              rules={[{ required: true, message: '请选择机器人类型' }]}
            >
              <Radio.Group>
                {BOT_TYPE_OPTIONS.map((opt) => {
                  const isSelected = selectedBotType === opt.code;
                  return (
                    <Radio.Button
                      key={opt.code}
                      value={opt.code}
                      style={{
                        marginRight: 16,
                        background: isSelected ? `${opt.color}33` : 'transparent',
                        borderColor: opt.color,
                        borderWidth: isSelected ? 2 : 1,
                        color: opt.color,
                        boxShadow: 'none',
                      }}
                    >
                      <Space>
                        <span style={{ fontWeight: isSelected ? 600 : 400 }}>{opt.label}</span>
                        <Text style={{ color: isSelected ? opt.color : '#666', fontSize: 12 }}>({opt.desc})</Text>
                      </Space>
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
            </Form.Item>

            <div style={{
              background: `${COLORS.borderDark}`,
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
            }}>
              <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 12, display: 'block' }}>默认画像设置</Text>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>角色类型</Text>} name="roleType">
                    <Select options={ROLE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>性别</Text>} name="gender">
                    <Select options={GENDER_OPTIONS.map((o) => ({ value: o.code, label: o.label }))} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>活跃度</Text>} name="activityLevel">
                    <Select options={ACTIVITY_OPTIONS.map((o) => ({ value: o.code, label: o.label }))} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <Form.Item>
              <Space>
                <Button onClick={handleCloseCreateModal}>取消</Button>
                <Button type="primary" htmlType="submit" loading={isCreating} style={{ background: COLORS.success, borderColor: COLORS.success }}>
                  确认创建
                </Button>
              </Space>
            </Form.Item>
        </Form>
      </Modal>

      {/* 模版编辑弹窗 */}
      <Modal
        title={
          <Space>
            <AppstoreOutlined style={{ color: COLORS.purple }} />
            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{editingTemplate ? '编辑模版' : '新建模版'}</span>
          </Space>
        }
        open={showTemplateModal}
        onCancel={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
          templateForm.resetFields();
        }}
        footer={null}
        width={700}
        styles={{ body: { paddingTop: 24 } }}
      >
        <Form
          form={templateForm}
          onFinish={handleSaveTemplate}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>模版名称</Text>}
                name="name"
                rules={[{ required: true, message: '请输入模版名称' }]}
              >
                <Input placeholder="请输入模版名称，如：KOL模版、新手模版" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>启用状态</Text>}
                name="enabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>模版描述</Text>}
            name="description"
          >
            <Input.TextArea rows={2} placeholder="请输入模版描述（可选）" />
          </Form.Item>

          <div style={{
            background: `${COLORS.borderDark}`,
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
          }}>
            <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 12, display: 'block' }}>画像属性设置</Text>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>角色类型</Text>} name="roleType">
                  <Select
                    options={ROLE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                    placeholder="请选择"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>性别</Text>} name="gender">
                  <Select
                    options={GENDER_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                    placeholder="请选择"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>说话风格</Text>} name="speakStyle">
                  <Select
                    options={SPEAK_STYLE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                    placeholder="请选择"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>活跃度</Text>} name="activityLevel">
                  <Select
                    options={ACTIVITY_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                    placeholder="请选择"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>经验年限</Text>} name="experienceYears">
                  <InputNumber min={0} max={30} style={{ width: '100%' }} placeholder="年" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={<Text style={{ color: '#6e7681', fontSize: 12 }}>地区</Text>} name="region">
                  <Input placeholder="如：中国" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={<Text style={{ color: '#6e7681', fontSize: 12 }}>兴趣标签</Text>}
              name="interests"
              style={{ marginBottom: 0 }}
            >
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择兴趣标签"
                options={interestTags}
                optionFilterProp="label"
                showSearch
              />
            </Form.Item>
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button onClick={() => {
                setShowTemplateModal(false);
                setEditingTemplate(null);
                templateForm.resetFields();
              }}>取消</Button>
              <Button type="primary" htmlType="submit" loading={savingTemplate} style={{ background: COLORS.secondary, borderColor: COLORS.secondary }}>
                {editingTemplate ? '保存修改' : '创建模版'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 画像编辑弹窗 */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: COLORS.cyan }} />
            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>编辑画像 - {editingProfile?.userName || `Bot#${editingProfile?.botId}`}</span>
          </Space>
        }
        open={showEditProfileModal}
        onCancel={handleCloseProfileModal}
        footer={null}
        width={600}
        styles={{ body: { paddingTop: 24 } }}
      >
        <Form
          form={profileForm}
          onFinish={handleSaveProfile}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>角色类型</Text>}
                name="roleType"
              >
                <Select
                  options={ROLE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                  placeholder="请选择角色类型"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>性别</Text>}
                name="gender"
              >
                <Select
                  options={GENDER_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                  placeholder="请选择性别"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>说话风格</Text>}
                name="speakStyle"
              >
                <Select
                  options={SPEAK_STYLE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                  placeholder="请选择说话风格"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>活跃度</Text>}
                name="activityLevel"
              >
                <Select
                  options={ACTIVITY_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                  placeholder="请选择活跃度"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>经验年限</Text>}
                name="experienceYears"
              >
                <InputNumber min={0} max={30} style={{ width: '100%' }} placeholder="请输入经验年限" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>地区</Text>}
                name="region"
              >
                <Input placeholder="请输入地区，如：中国、美国" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>兴趣标签</Text>}
            name="interests"
          >
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="选择兴趣标签"
              options={interestTags}
              optionFilterProp="label"
              showSearch
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button onClick={handleCloseProfileModal}>取消</Button>
              <Button type="primary" htmlType="submit" loading={savingProfile}>
                保存画像
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改头像 Modal */}
      <Modal
        title={`修改头像 - ${editingAvatarBot?.userName || `Bot#${editingAvatarBot?.botId}`}`}
        open={avatarModalVisible}
        onCancel={closeAvatarModal}
        onOk={handleConfirmAvatar}
        okText="确认修改"
        cancelText="取消"
        okButtonProps={{ disabled: !pendingAvatarUrl || avatarUploading, loading: avatarConfirming }}
        cancelButtonProps={{ disabled: avatarUploading || avatarConfirming }}
        maskClosable={!avatarUploading && !avatarConfirming}
        closable={!avatarUploading && !avatarConfirming}
        destroyOnClose
        width={520}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginBottom: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>当前</div>
            <Avatar
              size={72}
              src={editingAvatarBot?.avatar || undefined}
              icon={!editingAvatarBot?.avatar ? <UserOutlined /> : undefined}
            />
          </div>
          <div style={{ fontSize: 22, color: '#bbb' }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>预览</div>
            <Avatar
              size={72}
              src={pendingAvatarUrl || undefined}
              icon={!pendingAvatarUrl ? <UserOutlined /> : undefined}
            />
          </div>
        </div>

        <Upload.Dragger
          name="file"
          accept="image/*"
          multiple={false}
          showUploadList={false}
          beforeUpload={beforeAvatarUpload}
          customRequest={customAvatarUpload}
          disabled={avatarUploading || avatarConfirming}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            {avatarUploading ? '上传中…' : pendingAvatarUrl ? '已上传，可重新选择' : '点击或拖拽图片到此区域上传'}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12, color: '#888' }}>
            支持 jpg/png/gif/webp，单文件不超过 5MB；上传完成后请点击"确认修改"
          </p>
        </Upload.Dragger>

        {pendingAvatarUrl && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#888', wordBreak: 'break-all' }}>
            URL: <code>{pendingAvatarUrl}</code>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BotManagement;
